/**
 * The `POST /billing/subscribe` vocabulary.
 *
 * 🚨 **Read the request schema for what is _not_ in it.**
 *
 * There is no `price_id`, no `trial_period_days` and no `trial_days` — not as
 * optional fields, not as nullable ones, not "for testing". Today's contract
 * declares `trial_period_days: NullOr(Int)`, an unbounded integer forwarded
 * straight to `subscriptions.create` with no clamp, and `price_id` as a free
 * string; either one lets the caller choose what it pays and for how long it
 * pays nothing.
 *
 * The enforcement is **the absence of the field, not validation of it**. A field
 * that exists can be tampered with, forgotten in a validator, or re-added by a
 * well-meaning refactor; a field that does not exist cannot. QA overrides a trial
 * with a PostHog flag override on a test account, never a request field and never
 * a URL parameter.
 *
 * @see docs/projects/active/refactor-payments/refactor-everything-payments.md — `POST /billing/subscribe`
 */
import { Schema } from 'effect';
import { PlanKey } from './billing-status';

/**
 * Ad-attribution click ids, carried to the Stripe customer at creation.
 *
 * Deliberately part of the request rather than read from the session: marketing
 * reconciles ad spend against these on the customer object, and attribution
 * capture must not require a session — a paywall shown before signup still has
 * to record them. The server fills `_fbc`/`_fbp` from the request cookies when
 * the client omits them.
 */
export const CheckoutAttribution = Schema.Struct({
  google_click_id: Schema.optional(Schema.NullOr(Schema.String)),
  google_wbraid: Schema.optional(Schema.NullOr(Schema.String)),
  google_gbraid: Schema.optional(Schema.NullOr(Schema.String)),
  facebook_click_id: Schema.optional(Schema.NullOr(Schema.String)),
  facebook_click_cookie: Schema.optional(Schema.NullOr(Schema.String)),
  facebook_pixel_cookie: Schema.optional(Schema.NullOr(Schema.String)),
});
export type CheckoutAttribution = Schema.Schema.Type<
  typeof CheckoutAttribution
>;

export const UPLOADABLE_GOOGLE_CLICK_KEYS = [
  'google_click_id',
  'google_wbraid',
] as const;

export const GOOGLE_CLICK_KEYS = [
  ...UPLOADABLE_GOOGLE_CLICK_KEYS,
  'google_gbraid',
] as const;

export const SubscribeRequest = Schema.Struct({
  /** A plan, never a price. The server resolves the price by `lookup_key`. */
  plan_id: PlanKey,
  /**
   * A route token that unlocks a non-selectable plan — **not** a Stripe coupon
   * (these promos swap the price, so Stripe never learns a discount happened).
   * The server checks that the code actually unlocks the plan being requested.
   */
  promo_code: Schema.optional(Schema.NullOr(Schema.String)),
  attribution: Schema.optional(CheckoutAttribution),
  /**
   * Undo a pending cancel and nothing else — refused with a 400 when the
   * server would otherwise sell `plan_id`. What "Resubscribe" posts for a
   * price outside the catalog, whose `plan_id` can only be a stand-in.
   */
  uncancel_only: Schema.optional(Schema.Boolean),
});
export type SubscribeRequest = Schema.Schema.Type<typeof SubscribeRequest>;

/**
 * Which Stripe.js call the client must make next.
 *
 * The server decides, because the server is what chose between a trial (collect
 * a card for later — `confirmSetup`) and an immediate charge (`confirmPayment`).
 * The client must not infer it from whether a trial was offered: today the client
 * picks the endpoint and the confirm call, which makes server behaviour a
 * function of a client branch.
 */
export const ConfirmationType = Schema.Literals(['payment', 'setup']);
export type ConfirmationType = Schema.Schema.Type<typeof ConfirmationType>;

export const SubscribeResponse = Schema.Struct({
  /**
   * `null` only when nothing needs confirming — an uncancelled subscription, or
   * a subscription that activated immediately on a card already on file.
   */
  client_secret: Schema.NullOr(Schema.String),
  confirmation_type: Schema.NullOr(ConfirmationType),
  subscription_id: Schema.String,
});
export type SubscribeResponse = Schema.Schema.Type<typeof SubscribeResponse>;
